export interface Category {
  id: string;
  name: string;
  color: string;
  description: string;
  icon?: string;
}

export const CATEGORIES: Category[] = [
  {
    id: 'personal',
    name: 'Personal',
    color: '#ef4444', // Red
    description: 'Health, fitness, and wellbeing',
    icon: 'User'
  },
  {
    id: 'social',
    name: 'Social',
    color: '#f97316', // Orange
    description: 'Social interactions and relationships',
    icon: 'Users'
  },
  {
    id: 'occupational',
    name: 'Occupational',
    color: '#eab308', // Yellow
    description: 'Work-related tasks and career development',
    icon: 'Briefcase'
  },
  {
    id: 'avocational',
    name: 'Avocational',
    color: '#22c55e', // Green
    description: 'Hobbies and leisure activities',
    icon: 'Gamepad'
  },
  {
    id: 'ecological',
    // ⚠️⚠️ RENAMED "Ecological" → "Material" (owner, 2026-08-02). The ID STAYS
    // `ecological` on purpose: it keys every domain_stats row, every xp_titles
    // ladder row, guild/ability/marketplace records and ~51 code sites. Renaming
    // the id would orphan all of them for a change that is purely what the
    // domain is CALLED. See LEGACY_CATEGORY_NAMES below for the other half of
    // this — the old NAME is still out there in saved data.
    name: 'Material',
    color: '#3b82f6', // Blue
    description: 'Resources, possessions, finances, and living environment',
    icon: 'Package'
  },
  {
    id: 'intellectual',
    name: 'Intellectual',
    color: '#a855f7', // Purple
    description: 'Learning and mental growth activities',
    icon: 'Brain'
  },
  {
    id: 'existential',
    name: 'Existential',
    color: '#ec4899', // Pink
    description: 'Spiritual and philosophical pursuits',
    icon: 'Sparkles'
  }
];

/**
 * ⚠️⚠️ A DOMAIN'S DISPLAY NAME IS ALSO A STORED KEY — which is why renaming one
 * needs this map rather than just an edit above.
 *
 * `CATEGORIES[].name` is not only rendered. Six call sites RESOLVE a category by
 * matching a saved string against it: the four Life Balance surfaces (results
 * key on the domain's display name, and `assessment_questions.dimension` is
 * seeded with it) and three Marketplace surfaces (an item's `category` may hold
 * the name rather than the id). Rows written before a rename still carry the OLD
 * name, so without an alias they simply stop matching — and the failure is
 * SILENT: the lookup returns undefined and the surface falls back to a default
 * colour, so a renamed domain quietly loses its hue and icon instead of erroring.
 *
 * ⭐ Old name → current id. Add an entry here whenever a domain is renamed;
 * never delete one, because the data it covers does not expire.
 */
const LEGACY_CATEGORY_NAMES: Record<string, string> = {
  ecological: 'ecological', // was "Ecological", now displayed as "Material"
};

/** Every name a category has ever been called, lowercased. */
const NAME_TO_ID = new Map<string, string>();
for (const c of CATEGORIES) NAME_TO_ID.set(c.name.toLowerCase(), c.id);
for (const [legacy, id] of Object.entries(LEGACY_CATEGORY_NAMES)) NAME_TO_ID.set(legacy, id);

/**
 * The category ID a display name refers to, tolerating names it used to have.
 *
 * ⭐ RESOLVE TO AN ID, THEN LOOK UP IN WHATEVER LIST YOU HAVE. Most surfaces read
 * the USER's categories (they can recolour and even rename a domain in
 * Settings), so matching against the static defaults here and then finding by id
 * keeps a user's own customisation intact while still understanding a legacy
 * name that came out of storage.
 */
export function resolveCategoryId(name: string | null | undefined): string | undefined {
  if (!name) return undefined;
  return NAME_TO_ID.get(name.trim().toLowerCase());
}

/**
 * Find a DEFAULT category from a display name, tolerating legacy names.
 * ⚠️ Static list — use `resolveCategoryId` when the user's own list is at hand.
 */
export function findCategoryByName(name: string | null | undefined): Category | undefined {
  const id = resolveCategoryId(name);
  return id ? CATEGORIES.find(c => c.id === id) : undefined;
}

/**
 * The label to SHOW for a domain string that may be a stored legacy name.
 * Life Balance keeps its keys as the historical names, so its surfaces read
 * through this to display the current one.
 */
export function domainDisplayName(name: string): string {
  return findCategoryByName(name)?.name ?? name;
}
/**
 * ⭐⭐ **THE DOMAIN NAME IS OWNED HERE, NOT BY THE USER** (owner, 2026-08-13).
 * Settings no longer offers a name field — a user may recolour a domain and
 * change its icon, never rename it. This normalises a stored list so the name
 * and description always come from THIS file, while the user's own `color` and
 * `icon` survive untouched.
 *
 * ⚠️⚠️ **WITHOUT THIS, REMOVING THE EDITOR WOULD FREEZE A STALE NAME FOREVER.**
 * `user_categories.categories` is stored as whole rows, so anyone who had
 * already renamed a domain would keep that name with no way to change it back.
 *
 * ⚠️⚠️ **AND IT MASKED THE OWNER'S OWN RENAME.** "Ecological" → "Material" was
 * an owner call made in this file; a user who had renamed that domain never saw
 * it, because their stored row won. A user-editable display name is a second
 * source of truth for something this file is supposed to own.
 *
 * ⚠️ An id with no entry here is passed through untouched rather than dropped —
 * a category this build does not know about is not evidence it is invalid.
 */
export function withCanonicalNames(categories: Category[]): Category[] {
  return categories.map((c) => {
    const canonical = CATEGORIES.find((d) => d.id === c.id);
    return canonical
      ? { ...c, name: canonical.name, description: canonical.description }
      : c;
  });
}
